import React, { useState, useEffect } from "react";
import Head from "next/head";
import App from "next/app";
import "./globals.css";
import { ThemeProvider } from "@material-ui/styles";
import { Provider } from "react-redux";
import withReduxStore from "../lib/with-redux-store";
import theme from "../src/theme";
import Router from "next/router";
// import useModalStore from "../zustandStores/useModalStore";

const uuidv1 = require("uuid");

import {
  GA4_TRACKING_ID,
  PRODUCTION_URL,
  SESSION_TIME,
  STORAGE_ITEMS
} from "../services/constants";
import { injectStore } from "../services/FetchService";
import { REC_WEB_SESSION_START } from "../services/webengage/webEngageConstants";
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage
} from "../utils/localStorage";
import {
  isMobileDevice,
  // detectMobile,
  MOENGAGE_SCRIPT,
  CLARITY_SCRIPT,
  unregisterExistingSw,
  FB_PIXEL_SCRIPT
} from "../services/utils";
import IdleTimer from "../components/IdleTimer";
import { getCookie } from "../services/cookie";
import fetchUrl from "../services/fetchUrl";
import makeAjaxRequest from "../services/makeAjaxRequest";
import { SERVICE_WORKER } from "../routes/RouteConstants";
import ErrorBoundary from "../components/ErrorBoundary";
import TrackingService from "../services/TrackingService";
import { reportException } from "../logging";
import TopProgressBar from "../components/TopProgressBar";
import { StorageService } from "../services/StorageService";
import TooltipScheduler from "../components/TooltipScheduler";
import { resetTooltipStore } from "../utils/tooltiphelper";
import Script from "next/script";
import { createDeviceIdClient } from "../services/trackingUtils";
import { QueryClientProvider } from "@tanstack/react-query";
import { getQueryClient } from "../services/queryClientService";
import { JhToastProvider } from "designPackages/toastPackage";

export const queryClient = getQueryClient();

function MyApp({ Component, pageProps, reduxStore, initialReduxState }) {
  const [login, setLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // const setModalData = useModalStore(state => state.setModalData);

  // const callLogs = (nameObject, info) => {
  //   const deviceId = getCookie("recDeviceId");
  //   const url = fetchUrl("logs", "log-events");
  //   const env =
  //     process.env.LIVE_URL !== PRODUCTION_URL ? "staging" : "production";
  //   const data = [
  //     {
  //       event_category: nameObject.category,
  //       event_type: nameObject.name,
  //       event_info: JSON.stringify(info),
  //       source: detectMobile()
  //         ? `mobile-website-${env}`
  //         : `desktop-website-${env}`,
  //       user_type: "recruiter",
  //       deviceId
  //     }
  //   ];

  //   makeAjaxRequest({
  //     method: "POST",
  //     url,
  //     data
  //   }).catch(() => {
  //     console.log("Error in event log");
  //   });
  // };

  const callSessionLogs = (eventObj, recSessionId, trackingId = "") => {
    const deviceId = getCookie("recDeviceId");
    const accessToken = getCookie("rec_access_token");
    const url = fetchUrl("logs", "log-events");
    const env =
      process.env.LIVE_URL !== PRODUCTION_URL ? "staging" : "production";

    const data = [
      {
        event_category: eventObj.category,
        event_type: eventObj.name,
        source: isMobileDevice()
          ? `mobile-website-${env}`
          : `desktop-website-${env}`,
        user_type: "recruiter",
        event_info: JSON.stringify({
          session_id: recSessionId,
          trackingId: trackingId ? trackingId : undefined
        }),
        Authorization: accessToken,
        "device-id": deviceId
      }
    ];

    return makeAjaxRequest({
      method: "POST",
      url,
      data
    }).catch(() => {
      console.log("Error in event log");
    });
  };

  const startSessionEvent = () => {
    const recSessionId = getLocalStorage("recSessionId");
    callSessionLogs(REC_WEB_SESSION_START, recSessionId);
  };

  const setNewSession = isEvent => {
    const recSessionId = uuidv1();
    setLocalStorage("recSessionId", recSessionId);
    setLocalStorage("_recEexpiredTime", Date.now() + SESSION_TIME * 1000);
    if (isEvent) {
      startSessionEvent();
    }
  };

  const closeSession = isEvent => {
    setNewSession(isEvent);
  };

  // const increaseTabCount = () => {
  //   const oldTabCount = Number(getLocalStorage("recTabCount")) || 0;
  //   setLocalStorage("recTabCount", oldTabCount + 1);
  // };

  const addIdleTimer = () => {
    const timer = new IdleTimer({
      timeout: SESSION_TIME,
      onTimeout: () => {
        if (getLocalStorage("_recEexpiredTime")) {
          closeSession(false);
        }
      },
      onExpired: () => {
        console.log("session expired");
      }
    });
  };

  const newSessionEvent = () => {
    const isSessionExpired = !!getLocalStorage("recSessionExpired");
    if (isSessionExpired) {
      startSessionEvent();
      StorageService.removeFromStorage(STORAGE_ITEMS.EVENT_TRACKING_DATA_REC);
      removeLocalStorage("recSessionExpired");
    }
  };

  const onLoad = async () => {
    createDeviceIdClient();
    TrackingService.whatsAppClickTracking();
    let serviceWorkerErr = true;
    try {
      if ("serviceWorker" in navigator) {
        unregisterExistingSw({
          scope: `${process.env.LIVE_URL_HIRE}/`
        });
        const reg = await navigator.serviceWorker.register(SERVICE_WORKER, {
          scope: "/hire"
        });
        reg.update();
        console.log("ServiceWorker registration successful");
        serviceWorkerErr = false;
      }
    } catch (err) {
      const { response = {} } = err;

      if (serviceWorkerErr) {
        console.log("ServiceWorker registration failed", err);
      } else if (response.status !== 400) {
        reportException({ error: err });
      }
    }

    let recSessionId = getLocalStorage("recSessionId");
    const expiredTime = Number(getLocalStorage("_recEexpiredTime"));

    removeLocalStorage("recSessionExpired");

    // increaseTabCount();

    addIdleTimer();

    if (!recSessionId || Date.now() > expiredTime + SESSION_TIME * 1000) {
      setLocalStorage("recSessionExpired", true);
      setNewSession(true);
    }
  };

  useEffect(() => {
    injectStore(reduxStore);
    const userLoggedIn = getCookie("rec_access_token");
    if (userLoggedIn) {
      setLogin(true);
    }

    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles.parentNode.removeChild(jssStyles);
    }

    window.addEventListener("keydown", newSessionEvent);

    // Close modal on route change
    Router.events.on("routeChangeStart", () => {
      setIsLoading(true);
      // setModalData({ state: "closed", type: "" }); // todo
    });

    Router.events.on("routeChangeComplete", () => {
      setIsLoading(false);
      resetTooltipStore();
    });

    Router.events.on("routeChangeError", () => {
      setIsLoading(false);
    });

    document.onload = onLoad();

    // Load Sentry asynchronously to reduce initial bundle size (commented out - uncomment when re-enabling)
    // import("../sentry.client.config").catch(err => {
    //   console.error("Failed to load Sentry:", err);
    // });

    return () => {
      window.removeEventListener("keydown", newSessionEvent);
    };
  }, []);

  useEffect(() => {
    const loggedInCurrent = initialReduxState?.userProfileReducer?.isSuccess;
    if (loggedInCurrent && !login) {
      setLogin(true);
    }
    if (loggedInCurrent && login) {
      const recruiterId =
        initialReduxState?.userProfileReducer?.payload?.data?.recruiter_id;
      if (window.clarity && recruiterId) {
        window.clarity("identify", String(recruiterId));
      }
    }
  }, [initialReduxState?.userProfileReducer?.isSuccess]);

  const isThirdParty = process.env.BASE_URL === "https://api.jobhai.com";

  return (
    <React.Fragment>
      <Head>
        {login ? (
          <link
            href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
            rel="stylesheet"
          />
        ) : null}
        <meta
          name="viewport"
          content="minimum-scale=1.0, initial-scale=1.0, width=device-width, shrink-to-fit=no, maximum-scale=1.0"
        />
      </Head>
      {process.env.MOENGAGE_ID && login ? (
        <Script
          id="_moengage_script_rec"
          strategy="afterInteractive"
          onReady={() => {
            console.log("moengage script was loaded successfully");
          }}
          onError={() => {
            console.error("Error in loading moengage!!");
          }}
        >
          {MOENGAGE_SCRIPT}
        </Script>
      ) : null}
      {isThirdParty && login ? (
        <Script
          id="_clarity_script_rec"
          strategy="afterInteractive"
          onReady={() => {
            console.log("clarity script was loaded successfully");
          }}
          onError={() => {
            console.error("Error in loading clarity!!");
          }}
        >
          {CLARITY_SCRIPT}
        </Script>
      ) : null}
      {isThirdParty ? (
        <Script
          id="_fb_pixel_script_rec"
          strategy="afterInteractive"
          onReady={() => {
            console.log("Facebook Pixel script was loaded successfully");
          }}
          onError={() => {
            console.error("Error in loading Facebook Pixel!!");
          }}
        >
          {FB_PIXEL_SCRIPT}
        </Script>
      ) : null}
      {isThirdParty ? (
        <>
          <Script
            id="_gtag_script_rec"
            src={`https://www.googletagmanager.com/gtag/js?id=${GA4_TRACKING_ID}`}
            strategy="afterInteractive"
          />
          <Script id="_gtag_config_rec" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA4_TRACKING_ID}');
            `}
          </Script>
        </>
      ) : null}
      <ThemeProvider theme={theme}>
        <Provider store={reduxStore}>
          <JhToastProvider>
            <QueryClientProvider client={queryClient}>
              <ErrorBoundary>
                <TooltipScheduler />
                <Component {...pageProps} />
                {isLoading ? <TopProgressBar /> : null}
              </ErrorBoundary>
            </QueryClientProvider>
          </JhToastProvider>
        </Provider>
      </ThemeProvider>
    </React.Fragment>
  );
}

MyApp.getInitialProps = async appContext => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);

  return { ...appProps };
};

export default withReduxStore(MyApp);
